import { mixins, Button as ConsistentlyButton, Box, Stack, Grid } from "@fastly/consistently"
import styled, { css } from "styled-components"
import { withPrefix } from "gatsby"
import { MikanSpan } from "@components/Mikan"
import Intro from "@components/Intro/Intro"
import RichText from "@components/rich-text/renderer"

const { marginBlock, paddingBlock, paddingInline } = mixins

const light = css`
  h3,
  h6,
  p {
    color: var(--grey-80);
  }

  background: var(--grey-20);
`
const dark = css`
  h3 {
    color: var(--white);
    margin-bottom: 0;
  }

  h6,
  p {
    color: var(--grey-40);
  }

  a {
    color: var(--blue-light);
  }

  a:hover {
    color: var(--white);
  }

  background: var(--grey-80);
`
const theme = {
  light,
  dark,
}

const GridTextWrapper = styled(Stack)`
  ${({ variant }) => variant && theme[variant]}
  ${({ background }) => background && `background: ${background}`}
`

const GridTextItem = styled.div`
  // h6 should not be smaller than paragraph text on mobile devices
  --h6: 700 1rem/125% var(--font);
  grid-column: ${(props) => (props.intro ? `1/-1` : `auto`)};
  justify-self: center;
  ${marginBlock(0, "34px")}

  & p:empty {
    display: none;
  }

  & p {
    max-width: 548px;
    margin: 0 auto;
  }

  & p:nth-of-type(2) {
    margin-top: 1rem;
  }

  & h3 {
    ${(props) => props.textColor && `color: ${props.intro ? props.textColor.intro : "currentColor"}`};
  }

  & ul {
    list-style-type: none;
    padding-left: 0;
  }

  & li {
    background: url("/g-static/icons/icon-tick.svg") no-repeat;
    background-position: 0 1.4rem;
    ${paddingBlock("1rem", 0)}
    ${paddingInline("1.7rem", 0)}
    background-size: 1rem;
  }

  & b {
    color: ${(props) => (props.bg === "var(--grey-90)" ? `var(--white)` : `var(--grey-90)`)};
    font-weight: ${(props) => (props.bg === "var(--grey-90)" ? `500` : `700`)};
  }

  & img {
    ${marginBlock(0, "16px")}
  }

  @media (min-width: 768px) {
    text-align: ${(props) => (props.intro ? `center` : `left`)};
  }
`

const GridText = (
  {
    intro,
    items,
    button,
    background,
    columns = { _: "1", md: "2", lg: "2" },
    p = { _: "3.3rem 1.1rem", md: "6.4rem 4.2rem", lg: "6.4rem 13rem" },
    borderRadius = "16px",
    boxShadow,
    variant = "light",
  },
  { ...props }
) => {
  return (
    <GridTextWrapper
      background={background}
      borderRadius={borderRadius}
      boxShadow={boxShadow && "0px 16px 48px rgba(0, 0, 0, 0.06)"}
      p={p}
      variant={variant}
      {...props}
    >
      {intro && (
        <Intro type="main" align="center" spacing="sm">
          <RichText body={intro.rtContent} mikan={intro.mikan} />
        </Intro>
      )}
      {items && (
        <Grid sm={columns?._} md={columns?.md} lg={columns?.lg}>
          {items.section_content.map((item) => (
            <GridTextItem bg={background} cols={columns} key={item.contentful_id}>
              <RichText body={item.rtContent} mikan={item.mikan} />
            </GridTextItem>
          ))}
        </Grid>
      )}
      {button && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <ConsistentlyButton
            id={button.contentful_id}
            variant={button.variant}
            url={withPrefix(button.url)}
            data-type="button"
          >
            <MikanSpan text={button.buttonText} />
          </ConsistentlyButton>
        </Box>
      )}
    </GridTextWrapper>
  )
}

export default GridText
